import dayjs, { Dayjs } from 'dayjs';

const ID_OPCION_INCUMPLE = 2;

type SeleccionConFechas = {
    opciones: { id: number; selected: boolean }[];
    fechaRegularizacion?: string | null;
};

// Las fechas pueden venir como 'YYYY-MM-DD' o como datetime ISO en UTC: se truncan a
// los primeros 10 caracteres para comparar por día sin que la zona horaria corra la fecha.
const aDia = (fecha: string) => fecha.substring(0, 10);

// Mientras el preventor tipea, el DatePicker puede dejar guardado 'Invalid Date': como
// string queda por encima de cualquier 'YYYY-MM-DD', así que se descarta antes de comparar.
const esDiaValido = (dia: string) => /^\d{4}-\d{2}-\d{2}$/.test(dia) && dayjs(dia).isValid();

/**
 * SMP-430: fecha de regularización más tardía (YYYY-MM-DD) de los ítems "incumple" que
 * tienen una cargada, o `null` si ninguno la tiene.
 */
export const getFechaRegularizacionMasTardia = (seleccion: SeleccionConFechas[]): string | null =>
    seleccion
        .filter(
            (item) =>
                item.fechaRegularizacion &&
                item.opciones.some((opcion) => opcion.id === ID_OPCION_INCUMPLE && opcion.selected)
        )
        .map((item) => aDia(item.fechaRegularizacion as string))
        .filter(esDiaValido)
        .reduce<string | null>((max, dia) => (max === null || dia > max ? dia : max), null);

/**
 * Primer día elegible como fecha de seguimiento: el día siguiente a la regularización más
 * tardía, o `hoy` si es posterior.
 */
export const getFechaMinimaSeguimiento = (seleccion: SeleccionConFechas[], hoy: Dayjs): Dayjs => {
    const regularizacionMasTardia = getFechaRegularizacionMasTardia(seleccion);
    const hoyDia = hoy.startOf('day');
    if (!regularizacionMasTardia) return hoyDia;

    const diaSiguiente = dayjs(regularizacionMasTardia).add(1, 'day');
    return diaSiguiente.isAfter(hoyDia, 'day') ? diaSiguiente : hoyDia;
};

/**
 * La fecha de seguimiento debe ser al menos un día posterior a todas las fechas de
 * regularización de los ítems "incumple". Sin regularizaciones cargadas, no restringe.
 */
export const esFechaSeguimientoPosteriorARegularizaciones = (
    fechaSeguimiento: string,
    seleccion: SeleccionConFechas[]
): boolean => {
    const regularizacionMasTardia = getFechaRegularizacionMasTardia(seleccion);
    if (!regularizacionMasTardia) return true;

    return aDia(fechaSeguimiento) > regularizacionMasTardia;
};
